import { type ReactElement } from 'react';
import { UserPlus, Settings, Rocket, ArrowRight } from 'lucide-react';

interface Step {
  number: string;
  icon: typeof UserPlus;
  title: string;
  description: string;
  color: string;
}

const steps: Step[] = [
  {
    number: '01',
    icon: UserPlus,
    title: 'Registrieren',
    description:
      'Erstellen Sie in weniger als 2 Minuten Ihr kostenloses Konto. Keine Kreditkarte erforderlich.',
    color: 'primary',
  },
  {
    number: '02',
    icon: Settings,
    title: 'Einrichten',
    description:
      'Passen Sie Saldify an Ihr Unternehmen an. Importieren Sie bestehende Daten oder starten Sie frisch.',
    color: 'secondary',
  },
  {
    number: '03',
    icon: Rocket,
    title: 'Loslegen',
    description:
      'Erstellen Sie Ihre erste Rechnung und erleben Sie, wie einfach Buchhaltung sein kann.',
    color: 'success',
  },
];

export default function HowItWorks(): ReactElement {
  return (
    <section className="py-20 lg:py-32 bg-gradient-to-b from-secondary-50 to-white relative overflow-hidden">
      {/* Background Elements */}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-20">
          <span className="inline-block text-primary-600 font-semibold text-sm tracking-wider uppercase mb-4">
            So funktioniert's
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-secondary-900 mb-6">
            In 3 Schritten zur digitalen Buchhaltung
          </h2>
          <p className="text-lg text-secondary-600">
            Konto anlegen, erste Rechnung schreiben, Belege hinzufügen – mehr braucht
            es nicht, um loszulegen.
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connection Line (Desktop) */}
          <div className="hidden lg:block absolute top-24 left-[calc(16.67%+2rem)] right-[calc(16.67%+2rem)] h-0.5 bg-gradient-to-r from-primary-200 via-secondary-200 to-success-200" />

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {steps.map((step, index) => (
              <div
                key={step.number}
                className="relative group animate-slide-up"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                {/* Step Card */}
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-secondary-100">
                  {/* Step Number Badge */}
                  <div
                    className={`absolute -top-4 left-8 w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg ${
                      step.color === 'primary'
                        ? 'bg-primary-600 shadow-primary-600/30'
                        : step.color === 'secondary'
                        ? 'bg-secondary-600 shadow-secondary-600/30'
                        : 'bg-primary-600 shadow-primary-600/20'
                    }`}
                  >
                    {step.number}
                  </div>

                  {/* Icon */}
                  <div
                    className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mt-4 group-hover:scale-110 transition-transform ${
                      step.color === 'primary'
                        ? 'bg-primary-100'
                        : step.color === 'secondary'
                        ? 'bg-secondary-100'
                        : 'bg-primary-50'
                    }`}
                  >
                    <step.icon
                      className={`w-8 h-8 ${
                        step.color === 'primary'
                          ? 'text-primary-600'
                          : step.color === 'secondary'
                          ? 'text-secondary-600'
                          : 'text-success-600'
                      }`}
                    />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-secondary-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>

                {/* Arrow (Mobile/Tablet) */}
                {index < steps.length - 1 && (
                  <div className="flex justify-center my-6 md:hidden">
                    <ArrowRight className="w-6 h-6 text-secondary-300 rotate-90" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <a
            href="#signup"
            className="btn-primary px-8 py-4 text-base shadow-xl shadow-primary-600/30 hover:shadow-primary-600/50 hover:-translate-y-0.5 transition-all inline-flex items-center gap-2 group"
          >
            Jetzt kostenlos starten
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>
          <p className="text-secondary-500 text-sm mt-4">
            14 Tage kostenlos testen - Keine Kreditkarte erforderlich
          </p>
        </div>
      </div>
    </section>
  );
}
